/**
 * EntityRefDisplay — render reference đến entity (company/contact/deal/...) với:
 *  - Fetch name async qua GET /api/crm/{entity}/:id (cache 5 min)
 *  - Click → navigate đến entity page
 *  - Fallback: hiển thị UUID 8 chars khi loading/error
 *
 * Dùng cho FieldDisplay khi value của field là entity ID, để approval/UI nào
 * tham chiếu entity đều có link click được — TRÁNH plain text + UUID raw.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import api from '@host/axios';
import { CRM_ON } from '../../lib/hostModules';

import { useLingui } from '@lingui/react/macro';

// Map field type → API endpoint + UI route prefix
// type → { api: '/crm/companies/:id' template, route: '/crm/companies/' template }
const ENTITY_CONFIG = {
  company_select:  { api: 'crm/companies',   route: '/crm/companies'  },
  contact_select:  { api: 'crm/contacts',    route: '/crm/contacts'   },
  deal_select:     { api: 'crm/deals',       route: '/crm/deals'      },
  account_select:  { api: 'crm/accounts',    route: '/fulfillment/accounts' },
  customer_select: { api: 'app-store/customers', route: '/app-store/customers' },
  invoice_select:  { api: 'crm/invoices',    route: '/crm/invoices'   },
  payment_select:  { api: 'crm/payments',    route: '/crm/payments'   },
  project_select:  { api: 'projects',        route: '/projects'       },
  task_select:     { api: 'tasks',           route: '/tasks'          },
};

// Loại thuộc cụm CRM (gọi /crm/* + trang /crm/* · /fulfillment/* · /app-store/*). Hệ TẮT CRM (Site Connect 12/10/2026 —
// lib/hostModules, máy chủ chặn /crm/*): KHÔNG hỏi tên, KHÔNG link — chỉ hiện tên lưu sẵn hoặc 8 ký tự đầu của id.
const CRM_TYPES = new Set(['company_select', 'contact_select', 'deal_select', 'account_select', 'customer_select', 'invoice_select', 'payment_select']);
const isOffType = (fieldType) => !CRM_ON && CRM_TYPES.has(fieldType);

// Cho generic ref (vd partner_owner: type='company'|'contact')
const TYPE_TO_FIELD_TYPE = {
  company: 'company_select',
  contact: 'contact_select',
  deal: 'deal_select',
  account: 'account_select',
};

// In-memory cache: key = `${fieldType}:${id}` → { name, expiresAt }
const cache = new Map();
const TTL_MS = 5 * 60 * 1000;

async function fetchEntity(fieldType, id) {
  const key = `${fieldType}:${id}`;
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.data;

  const cfg = ENTITY_CONFIG[fieldType];
  if (!cfg || isOffType(fieldType)) return null;

  try {
    const r = await api.get(`/${cfg.api}/${id}`);
    const data = r.data?.data || r.data || {};
    // Most CRM endpoints trả { id, name, ... } hoặc { id, title, ... }
    const name = data.name || data.title || data.full_name || data.serial_number || null;
    const result = { name, raw: data };
    cache.set(key, { data: result, expiresAt: Date.now() + TTL_MS });
    return result;
  } catch (e) {
    return null;
  }
}

/**
 * Single entity render
 * @param {string} fieldType — vd 'company_select', 'contact_select'
 *   Cho generic (vd partner_owner_type='company'), pass `genericType` thay vì fieldType.
 * @param {string} id — UUID entity
 * @param {string} genericType — 'company' | 'contact' | 'deal' | 'account' (alternative to fieldType)
 * @param {string} fallbackName — name lưu trong form_data (nếu có) → render khi fetch chưa xong
 */
export default function EntityRefDisplay({ fieldType, genericType, id, fallbackName, multi = false, className = '' }) {
  const {
    t
  } = useLingui();

  // Resolve actual fieldType
  const resolvedType = fieldType || (genericType && TYPE_TO_FIELD_TYPE[genericType]);

  const [data, setData] = useState(() => (fallbackName ? { name: fallbackName } : null));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id || !resolvedType || isOffType(resolvedType)) return;
    let cancelled = false;
    setLoading(true);
    fetchEntity(resolvedType, id).then(r => {
      if (cancelled) return;
      setLoading(false);
      if (r) setData(r);
    });
    return () => { cancelled = true; };
  }, [resolvedType, id]);

  if (!id) return <span className="text-gray-400">—</span>;
  const cfg = ENTITY_CONFIG[resolvedType];
  if (!cfg || isOffType(resolvedType)) {
    // Unknown type — render fallback
    return <span className={`text-sm text-gray-700 ${className}`}>{fallbackName || String(id).slice(0, 8)}</span>;
  }

  const href = `${cfg.route}/${id}`;
  const displayName = data?.name || fallbackName || String(id).slice(0, 8);

  return (
    <Link to={href}
      className={`inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline ${className}`}
      title={t`Mở ${displayName}`}>
      <span className="truncate">{displayName}</span>
      {loading && !data && <Loader2 size={11} className="animate-spin shrink-0" />}
      <ExternalLink size={11} className="shrink-0 opacity-60" />
    </Link>
  );
}

/**
 * Multi-render — list of entity IDs.
 */
export function EntityRefList({ fieldType, genericType, ids = [], fallbackNames = {}, className = '' }) {
  if (!ids?.length) return <span className="text-gray-400">—</span>;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {ids.map(id => (
        <EntityRefDisplay key={id} fieldType={fieldType} genericType={genericType} id={id}
          fallbackName={fallbackNames[id]} className="px-1.5 py-0.5 bg-blue-50 rounded-md" />
      ))}
    </div>
  );
}
